import {
  Text as OriginalText,
  type TextProps as OriginalTextProps,
} from '@radix-ui/themes';
import { cva, type VariantProps } from 'class-variance-authority';
import React from 'react';

import { cn } from '@/lib/utils';

const textStyle = cva('text-foreground', {
  variants: {
    lowContrast: {
      true: 'text-foreground-muted',
    },
    size: {
      '1': 'text-xs',
      '2': 'text-sm',
      '3': 'text-md',
      '4': 'text-lg',
      '5': 'text-xl',
      '6': 'text-2xl',
      '7': 'text-3xl',
      '8': 'text-4xl',
      '9': 'text-5xl',
      xs: 'text-xs',
      sm: 'text-sm',
      md: 'text-md',
      lg: 'text-lg',
      xl: 'text-xl',
      '2xl': 'text-2xl',
      '3xl': 'text-3xl',
      '4xl': 'text-4xl',
      '5xl': 'text-5xl',
      '6xl': 'text-6xl',
      '7xl': 'text-7xl',
    },
    fontWeight: {
      normal: 'font-normal',
      medium: 'font-medium',
      bold: 'font-bold',
      semibold: 'font-semibold',
    },
    variant: {
      default: '',
      muted: 'text-foreground-muted',
      subtle: 'text-foreground-subtle',
      accent: 'text-accent-foreground',
    },
    ellipsis: {
      true: 'text-ellipsis',
    },
    nowrap: {
      true: 'whitespace-nowrap',
    },
    fontFamily: {
      sans: 'font-sans',
      serif: 'font-serif',
      mono: 'font-mono',
    },
    constrainWidth: {
      // the parent must be a flex
      true: 'w-0 flex-grow',
    },
  },
});

export type TextProps = OriginalTextProps &
  VariantProps<typeof textStyle> & {
    handleOverflow?: boolean;
  };

export const Text = React.forwardRef<HTMLParagraphElement, TextProps>(
  (
    {
      lowContrast,
      ellipsis,
      nowrap,
      handleOverflow,
      fontFamily,
      variant,
      size,
      fontWeight,
      className,
      constrainWidth,
      ...props
    },
    ref
  ) => {
    return (
      <OriginalText
        className={cn(
          textStyle({
            lowContrast,
            ellipsis,
            nowrap,
            fontFamily,
            constrainWidth,
            variant,
            fontWeight,
            size,
          }),
          className
        )}
        // coz originally chakra ui was being used, so this keeps things consistent
        as="p"
        ref={ref}
        truncate={handleOverflow}
        {...props}
      />
    );
  }
);
